import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { GroupFilterSelect, UNGROUPED_LABEL } from '@/components/form/GroupFilterSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { SortableHeader } from '@/components/SortableHeader';
import { useSession } from '@/hooks/useSession';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useGroupOptions } from '@/hooks/useGroupOptions';
import { useColumnSort, sortRows } from '@/lib/tableSort';
import { showToast } from '@/lib/toast';
import { formatDate, formatBalance } from '@/lib/clientFormat';
import {
  fetchClientPrices,
  fetchClientTestGroups,
  updateClientTest,
  type ClientPriceRow,
  type ClientsPageMeta,
} from '@/api/clients';

type PriceScope = 'library' | 'monoscale';

const PRICE_SCOPES: { key: PriceScope; label: string }[] = [
  { key: 'library', label: 'Тесты' },
  { key: 'monoscale', label: 'Моношкальные тесты' },
];

type PriceSortKey =
  | 'name'
  | 'version'
  | 'category'
  | 'basePrice'
  | 'clientPrice'
  | 'modifiedAt'
  | 'modifiedBy';

/**
 * Вкладка «Цены»: две вложенные вкладки – «Тесты» (только подключённые
 * библиотечные) и «Моношкальные тесты» (все, они доступны всегда). Таблица
 * один в один со старой админкой: тест / базовая цена / цена для клиента
 * (редактируется по клику на карандаш) / изменено / кем изменено (последнее –
 * best-effort, см. докблок CustomerController::prices()).
 */
export function PricesPanel({ accountId }: { accountId: number }) {
  const { user } = useSession();
  const [scope, setScope] = useState<PriceScope>('library');
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [group, setGroup] = useState('');
  const [page, setPage] = useState(1);
  const { groups, ungroupedValue } = useGroupOptions(
    () => fetchClientTestGroups(accountId, scope),
    [accountId, scope],
  );
  const [rows, setRows] = useState<ClientPriceRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Сортировка по столбцу — в пределах ТЕКУЩЕЙ страницы (см. докблок
  // CustomerController::prices() в Laravel: сервер отдаёт фиксированный
  // порядок по названию, произвольный sort= не поддерживает). Раньше грузился
  // весь список разом, и сортировка была глобальной; после перевода на
  // постраничную загрузку это осознанный компромисс, не регрессия молча.
  const sort = useColumnSort<PriceSortKey>();
  const sortedRows = useMemo(
    () => sortRows(rows ?? [], sort.key, sort.dir),
    [rows, sort.key, sort.dir],
  );

  // Новый scope/поиск/фильтр — начинаем с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [scope, search, group]);

  useEffect(() => {
    setRows(null);
    let cancelled = false;
    fetchClientPrices(accountId, { scope, search: search || undefined, group: group || undefined, page })
      .then(({ items, meta: pageMeta }) => {
        if (!cancelled) {
          setRows(items);
          setMeta(pageMeta);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить таблицу цен.');
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, scope, search, group, page]);

  const saveCost = async (row: ClientPriceRow, cost: number) => {
    if (cost === row.clientPrice) return;
    setPendingId(row.id);
    setError(null);
    try {
      await updateClientTest(accountId, row.id, { cost });
      setRows(
        (prev) =>
          prev?.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  clientPrice: cost,
                  modifiedAt: new Date().toISOString(),
                  modifiedBy: user.name ?? user.email,
                }
              : r,
          ) ?? prev,
      );
      showToast(`Цена для «${row.name}» изменена на ${formatBalance(cost)}`);
    } catch {
      setError(`Не удалось сохранить цену для «${row.name}».`);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div>
      {/* Тот же стиль, что верхние вкладки карточки (tabs__item), а не пилюли –
          это тоже переключение вкладок, просто вложенное. */}
      <div className="tabs__list tabs__list--nested" role="tablist">
        {PRICE_SCOPES.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={scope === s.key}
            className={`tabs__item${scope === s.key ? ' tabs__item--active' : ''}`}
            onClick={() => {
              setScope(s.key);
              setGroup('');
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="filter-row">
        <SearchBox value={searchInput} onChange={setSearchInput} />
        <GroupFilterSelect
          groups={groups}
          ungroupedValue={ungroupedValue}
          value={group}
          onChange={setGroup}
        />
      </div>

      {error && <p className="field-error">{error}</p>}

      {rows === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : (
        <div className="card card--flush">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <SortableHeader label="Тест" sortKey="name" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Версия" sortKey="version" sort={sort} onSort={sort.toggle} />
                  <SortableHeader label="Группа" sortKey="category" sort={sort} onSort={sort.toggle} />
                  <SortableHeader
                    label="Базовая цена"
                    sortKey="basePrice"
                    sort={sort}
                    onSort={sort.toggle}
                  />
                  <SortableHeader
                    label="Цена для клиента"
                    sortKey="clientPrice"
                    sort={sort}
                    onSort={sort.toggle}
                  />
                  <SortableHeader
                    label="Изменено"
                    sortKey="modifiedAt"
                    sort={sort}
                    onSort={sort.toggle}
                  />
                  <SortableHeader
                    label="Кем изменено"
                    sortKey="modifiedBy"
                    sort={sort}
                    onSort={sort.toggle}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="table__empty">
                      {scope === 'library'
                        ? 'Тесты ещё не подключены.'
                        : 'Моношкальных тестов нет.'}
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => (
                    <tr key={row.id}>
                      <td className="cell-primary">{row.name}</td>
                      <td className="cell-muted">{row.version ?? '–'}</td>
                      <td className="cell-muted">{row.category ?? UNGROUPED_LABEL}</td>
                      <td className="cell-muted tabular">{formatBalance(row.basePrice)}</td>
                      <td className="tabular">
                        <PriceCell row={row} disabled={pendingId === row.id} onSave={saveCost} />
                      </td>
                      <td className="cell-muted">
                        {row.modifiedAt ? formatDate(row.modifiedAt) : '–'}
                      </td>
                      <td className="cell-muted">{row.modifiedBy ?? '–'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {meta && meta.lastPage > 1 && (
        <div className="table-footer">
          <span className="table-footer__status">
            Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
          </span>
          <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

/**
 * Цена для клиента – статичный текст + карандаш, по клику превращается в
 * поле ввода (как price-static/edit-icon-btn в референсе), а не всегда
 * открытый инпут.
 */
function PriceCell({
  row,
  disabled,
  onSave,
}: {
  row: ClientPriceRow;
  disabled: boolean;
  onSave: (row: ClientPriceRow, cost: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(String(row.clientPrice));

  if (editing) {
    return (
      <input
        type="number"
        min={0}
        autoFocus
        className="test-list__cost-input"
        value={local}
        disabled={disabled}
        onChange={(e) => setLocal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setLocal(String(row.clientPrice));
            setEditing(false);
          }
        }}
        onBlur={() => {
          setEditing(false);
          const n = Number(local);
          if (Number.isFinite(n) && n >= 0) onSave(row, n);
          else setLocal(String(row.clientPrice));
        }}
        aria-label={`Цена для клиента: «${row.name}»`}
      />
    );
  }

  return (
    <span className="price-cell">
      <b>{formatBalance(row.clientPrice)}</b>
      <button
        type="button"
        className="price-cell__edit"
        disabled={disabled}
        onClick={() => {
          setLocal(String(row.clientPrice));
          setEditing(true);
        }}
        aria-label={`Изменить цену для клиента: «${row.name}»`}
      >
        <Icon name="pencil" size={13} />
      </button>
    </span>
  );
}
